/**
 * @file Pure page/section management logic — zero DOM dependency.
 *
 * During Vue migration → Pinia store actions:
 *   - createPage      → usePagesStore().addPage()
 *   - createSection   → usePagesStore().addSection()
 *   - reorderSection  → usePagesStore().reorder()
 *   - getBuildStatus  → usePagesStore().buildStatus (computed)
 */
import type { ConceptSection, Page, Section, SectionType, SectionTypeId } from '../types.ts';

// ── PAGE CREATION ──────────────────────────────────────────────────

/**
 * Create a new page object with shared header/footer from home page.
 * Returns null if name is empty.
 */
export const createPage = (
  name: string,
  pages: Page[],
  sectionIdFn: (type: SectionTypeId, sects: Section[]) => string,
): Page | null => {
  if (!name?.trim()) {
    return null;
  }

  const slug = name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

  const shared: Section[] = [];
  const sharedSources = (pages[0]?.sects ?? []).filter(s => s.type === 'header' || s.type === 'footer');
  for (const s of sharedSources) {
    shared.push({
      id: sectionIdFn(s.type, shared),
      type: s.type,
      title: s.title,
      purpose: s.purpose,
      html: s.html || '',
      status: (s.html ? 'done' : 'pending') as Section['status'],
      note: '',
      error: '',
      replaceImg: null,
      screenshotImg: null,
      editLog: [],
    });
  }

  return {
    id: `page-${slug}-${Date.now()}`,
    name: name.trim(),
    sects: shared,
    shellHtml: '',
    shellStatus: 'pending',
    assembled: '',
    editHist: [],
    editUndoStack: [],
    chatLog: [],
  };
};

// ── SECTION CREATION ───────────────────────────────────────────────

/**
 * Create a new section from a section type definition.
 */
export const createSection = (
  typeId: SectionTypeId,
  sectionTypes: SectionType[],
  existingSects: Section[],
  sectionIdFn: (type: SectionTypeId, sects: Section[]) => string,
): { section: Section; conceptSection: ConceptSection } => {
  const def = sectionTypes.find(t => t.id === typeId) || {
    id: typeId,
    color: '#64748b',
    desc: '',
  };
  const title = def.id.charAt(0).toUpperCase() + def.id.slice(1).replace(/-/g, ' ');
  const id = sectionIdFn(typeId, existingSects);

  const section: Section = {
    id,
    type: def.id,
    title,
    purpose: def.desc,
    html: '',
    status: 'pending',
    note: '',
    error: '',
    replaceImg: null,
    screenshotImg: null,
    editLog: [],
  };

  const conceptSection: ConceptSection = {
    id,
    type: def.id,
    title,
    purpose: def.desc,
    visualBrief: '',
    layout: 'auto',
    bgStyle: 'light',
    copyNotes: '',
  };

  return { section, conceptSection };
};

// ── SECTION REORDER ────────────────────────────────────────────────

/**
 * Reorder a section within the array (drag-and-drop result).
 * Mutates the array in place.
 */
export const reorderSection = (
  sects: Section[],
  fromIdx: number,
  toIdx: number,
  above: boolean,
): void => {
  let target = above ? toIdx : toIdx + 1;
  if (fromIdx < target) {
    target--;
  }
  const [moved] = sects.splice(fromIdx, 1);
  target = Math.max(0, Math.min(target, sects.length));
  sects.splice(target, 0, moved);
};

// ── BUILD STATUS ───────────────────────────────────────────────────

export interface BuildStatus {
  pending: number;
  done: number;
  total: number;
  pct: number;
  allDone: boolean;
  canExport: boolean;
}

/**
 * Calculate build progress for a page.
 */
export const getBuildStatus = (page: Page, activePageIdx: number): BuildStatus => {
  const isExtraPage = activePageIdx > 0;
  const buildable = isExtraPage
    ? page.sects.filter(s => s.type !== 'header' && s.type !== 'footer')
    : page.sects;

  const pending = buildable.filter(s => s.status === 'pending').length;
  const done = buildable.filter(s => s.status === 'done').length;
  const building = buildable.find(s => s.status === 'building');
  const total = buildable.length;

  const sharedReady =
    !isExtraPage ||
    page.sects
      .filter(s => s.type === 'header' || s.type === 'footer')
      .every(s => s.status === 'done');

  const allDone = total > 0 && done === total && sharedReady && page.shellStatus === 'done';
  const pct = page.shellStatus === 'done' && total > 0 ? Math.round((done / total) * 100) : 0;
  const canExport = done > 0 && !building && page.shellStatus === 'done';

  return { pending, done, total, pct, allDone, canExport };
};
